import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SiteHeader } from "@/components/Logo";
import { CheckCircle2, XCircle, Loader2, LogIn } from "lucide-react";
import { verifyMagicLink, setAuth } from "@/lib/authApi";
import { Button } from "@/components/ui/button";

// Module-level cache: keyed by token → the settled verify result (or in-flight
// promise). Guarantees double-clicks / StrictMode double-mounts only ever fire
// ONE server call per token.
const verifyCache = new Map(); // token -> { promise, result?, error? }

function getOrStartVerify(token) {
  let entry = verifyCache.get(token);
  if (!entry) {
    entry = { promise: null, result: null, error: null };
    verifyCache.set(token, entry);
    entry.promise = verifyMagicLink(token)
      .then((r) => {
        entry.result = r;
        return r;
      })
      .catch((e) => {
        entry.error = e;
        throw e;
      });
  }
  if (entry.result) return Promise.resolve(entry.result);
  if (entry.error) return Promise.reject(entry.error);
  return entry.promise;
}

// Landing page for the /auth/verify?token=… magic link.
// IMPORTANT: sign-in requires a button tap — email security scanners
// (Gmail/Outlook link protection) prefetch links and would otherwise consume
// the one-time token before the human ever taps it.
export default function AuthVerify() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const next = params.get("next") || "/";
  const [status, setStatus] = useState(token ? "ready" : "error");
  const [errorMsg, setErrorMsg] = useState(token ? "" : "Missing token in link.");

  const confirmSignIn = () => {
    setStatus("verifying");
    getOrStartVerify(token)
      .then(({ session_token, user }) => {
        setAuth({ session_token, user });
        setStatus("ok");
        toast.success(`Signed in as ${user.email}`);
        setTimeout(() => nav(next, { replace: true }), 800);
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Verification failed.";
        setStatus("error");
        setErrorMsg(msg);
      });
  };

  return (
    <div className="min-h-screen bg-pbo-paper">
      <SiteHeader />
      <div className="max-w-md mx-auto px-6 py-16">
        <div
          className="bg-white border border-pbo-border rounded-2xl p-8 shadow-sm text-center"
          data-testid={`verify-${status}`}
        >
          {status === "ready" && (
            <>
              <div className="w-14 h-14 rounded-full bg-pbo-green/10 text-pbo-green grid place-items-center mx-auto mb-4">
                <LogIn className="w-7 h-7" />
              </div>
              <h1 className="font-serif text-pbo-green text-2xl mb-2">
                Almost there
              </h1>
              <p className="text-pbo-ink/70 text-sm mb-6">
                Tap below to finish signing in on this device.
              </p>
              <Button
                onClick={confirmSignIn}
                className="bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream w-full h-12 rounded-full font-semibold"
                data-testid="verify-confirm-btn"
              >
                Sign me in
              </Button>
            </>
          )}
          {status === "verifying" && (
            <>
              <Loader2 className="w-10 h-10 text-pbo-green mx-auto mb-4 animate-spin" />
              <h1 className="font-serif text-pbo-green text-2xl mb-2">
                Signing you in…
              </h1>
              <p className="text-pbo-ink/70 text-sm">One moment.</p>
            </>
          )}
          {status === "ok" && (
            <>
              <CheckCircle2 className="w-12 h-12 text-pbo-green mx-auto mb-4" />
              <h1 className="font-serif text-pbo-green text-2xl mb-2">
                You&apos;re in.
              </h1>
              <p className="text-pbo-ink/70 text-sm">Redirecting…</p>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h1 className="font-serif text-pbo-green text-2xl mb-2">
                Link didn&apos;t work
              </h1>
              <p className="text-pbo-ink/70 text-sm mb-4">{errorMsg}</p>
              <Button
                onClick={() => nav("/login")}
                className="bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream"
                data-testid="verify-request-new-btn"
              >
                Request a new link
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
